import { defineStore } from "pinia";
import Vue from "vue";
import BILLING_SETTINGS_QUERY from "@/graphql/accountSettings/billingSettings.graphql";
import INVOICES_QUERY from "@/graphql/accountSettings/invoices.graphql";
import NOTIFICATION_SETTINGS_QUERY from "@/graphql/accountSettings/notificationSettings.graphql";
import PROFILE_SETTINGS_QUERY from "@/graphql/accountSettings/profileSettings.graphql";
import CONNECTED_ACCOUNTS_QUERY from "@/graphql/accountSettings/connectedAccounts.graphql";
import PERSONAL_DETAILS_QUERY from "@/graphql/accountSettings/personalDetails.graphql";
import { useUserStore } from "~/stores/user";
import { requestInFlight, trackRequest } from "~/stores/user/inFlightRequests";

const QUERIES = {
  billing: BILLING_SETTINGS_QUERY,
  invoices: INVOICES_QUERY,
  notifications: NOTIFICATION_SETTINGS_QUERY,
  profile: PROFILE_SETTINGS_QUERY,
  connectedAccounts: CONNECTED_ACCOUNTS_QUERY,
  personalDetails: PERSONAL_DETAILS_QUERY,
};

const SECTIONS = Object.keys(QUERIES);

const emptySections = () =>
  Object.fromEntries(SECTIONS.map((section) => [section, null]));

const belongsTo = (data, me) =>
  !!data && !!me && String(data.id) === String(me.id);

const sectionOf = (state, section) =>
  belongsTo(state.loaded[section], useUserStore().me)
    ? state.loaded[section]
    : null;

let lastVisit = 0;

/**
 * The data of the settings page cards, one query per section, kept out of
 * `me` (`graphql/accountSettings/*.graphql`):
 * - `billing`: `balance`, `storedBankcards` (Billing card)
 * - `invoices`: `invoicesV2`, loaded when the invoice list is opened
 * - `notifications`: the four notification flags (Notification card)
 * - `profile`: `memo`, `showZoneMap`, `hideMobile`, `limitPriceInfo` (Profile card)
 * - `connectedAccounts`: `connectedOauthProviders` (settings page, Connected accounts card)
 * - `personalDetails`: `phoneNumber` and the billing details (Account and
 *   Billing cards); the price-offer preview and the designer's tender form
 *   load it too
 *
 * The settings page starts a visit when it is created and ends it when it is
 * destroyed, so each visit loads fresh data. Other pages load only
 * `personalDetails`, which stays until the next visit starts.
 */
export const useAccountSettingsStore = defineStore("accountSettingsStore", {
  state: () => ({
    visit: 0,
    loaded: emptySections(),
    pending: emptySections(),
  }),
  getters: {
    billing: (state) => sectionOf(state, "billing"),
    invoices: (state) => sectionOf(state, "invoices"),
    notifications: (state) => sectionOf(state, "notifications"),
    profile: (state) => sectionOf(state, "profile"),
    connectedAccounts: (state) => sectionOf(state, "connectedAccounts"),
    personalDetails: (state) => sectionOf(state, "personalDetails"),
    /**
     * Whether a request for the section is in flight.
     * @returns {(section: string) => boolean}
     */
    isLoading(state) {
      return (section) =>
        !!requestInFlight(this, section, state.pending[section]);
    },
  },
  actions: {
    /**
     * Forgets the data of the previous visit and any request in flight.
     * @returns {number} the visit, to pass to `endVisit`
     */
    startVisit() {
      lastVisit += 1;
      this.$reset();
      this.visit = lastVisit;
      return lastVisit;
    },

    /**
     * Forgets the data, unless a newer visit has started since.
     * @param {number} visit
     */
    endVisit(visit) {
      if (this.visit === visit) {
        this.$reset();
      }
    },

    /**
     * Loads a section once per visit, without sending a second request while
     * one is in flight.
     * Requests in flight, also a refresh of loaded data or one that replaced
     * this call's own request, are waited for, so the call never returns with
     * data that is about to be replaced or without the data a newer request
     * brings.
     * @param {string} section
     */
    async load(section) {
      if (!useUserStore().me) {
        return;
      }
      if (
        !this[section] &&
        !requestInFlight(this, section, this.pending[section])
      ) {
        await this.sendQuery(section);
      }
      let pending = requestInFlight(this, section, this.pending[section]);
      while (pending) {
        await pending.catch(() => {});
        pending = requestInFlight(this, section, this.pending[section]);
      }
    },

    /**
     * Reloads a section. A response that arrives after a newer request was
     * sent, or after `setValues`, is dropped.
     * @param {string} section
     */
    async refresh(section) {
      if (!useUserStore().me) {
        return;
      }
      await this.sendQuery(section);
    },

    /**
     * Reloads the sections that are loaded or loading, for callers that
     * changed their data.
     * @param {string[]} [sections]
     */
    async refreshIfLoaded(sections = SECTIONS) {
      await Promise.all(
        sections
          .filter(
            (section) =>
              this[section] ||
              requestInFlight(this, section, this.pending[section])
          )
          .map((section) => this.refresh(section))
      );
    },

    /**
     * Applies the result of a mutation to a loaded section.
     * @param {string} section
     * @param {object} values
     */
    setValues(section, values) {
      const data = this[section];
      if (!data) {
        return;
      }
      this.pending[section] = null;
      for (const [key, value] of Object.entries(values)) {
        if (!key.startsWith("__")) {
          Vue.set(data, key, value);
        }
      }
    },

    async sendQuery(section) {
      const userStore = useUserStore();
      const query = this.$nuxt.$query(
        QUERIES[section],
        {},
        { skipErrorPageOnAuthError: true }
      );
      const id = trackRequest(this, section, query);
      this.pending[section] = id;
      try {
        const result = await query;
        if (
          this.pending[section] === id &&
          belongsTo(result?.me, userStore.me)
        ) {
          this.loaded[section] = result.me;
        }
      } catch (error) {
        this.$nuxt.$logger.warn(
          `Failed to load the ${section} settings`,
          error
        );
      } finally {
        if (this.pending[section] === id) {
          this.pending[section] = null;
        }
      }
    },
  },
});
